import { Product, ProductType } from '@prophecy/graphql-types/enums';
import { getBasePath, history, setBasePath } from '@prophecy/utils/history';
import { matchPath } from 'react-router-dom';

export const METADATA_BASE = '/metadata';
export const FINANCE_BASE = '/finance';
export const PROFESSIONAL_BASE = '/professional';

/**
 * Returns the active base path (/metadata | /finance | /professional).
 *
 * Two calling conventions:
 *
 * 1. No argument — "what base is currently active?"
 *    Prefers the globalState value written by setBasePath().
 *    setBasePath() is called immediately when the intended base changes (e.g. by
 *    getAppMetadata when vertical-apps config is loaded), BEFORE window.location
 *    reflects the new base.  Reading window.location here would return the stale
 *    base and cause getUrlWithBasePath() to build a mismatched prefix that
 *    historyReplace() then double-prepends (→ /professional/metadata/).
 *
 * 2. Explicit URL string — parse that URL and return its base segment.
 *    Used when we deliberately need to inspect a specific URL (e.g. index.tsx
 *    bootstrapping, replaceBasePath computing the sub-path to preserve).
 */
export function getBasePathFromUrl(url?: string): string {
  if (url === undefined) {
    // No URL supplied: trust globalState over window.location.
    const stored = getBasePath();
    if (stored === METADATA_BASE || stored === FINANCE_BASE || stored === PROFESSIONAL_BASE) {
      return stored;
    }
    // Fall through to parse window.location if globalState hasn't been set yet.
  }

  const pathname = url ?? window.location.pathname;
  let base = METADATA_BASE;
  if (matchPath(`${FINANCE_BASE}/*`, pathname)) {
    base = FINANCE_BASE;
  }
  if (matchPath(`${PROFESSIONAL_BASE}/*`, pathname)) {
    base = PROFESSIONAL_BASE;
  }
  return base;
}

export function isVerticalAppRoute() {
  const base = getBasePathFromUrl();
  return base === FINANCE_BASE || base === PROFESSIONAL_BASE;
}

export function isProductTypeMatching(productType: ProductType) {
  const base = getBasePathFromUrl();
  return base === `/${productType.toLowerCase()}`;
}

export function replaceBasePath(newBasePath: string) {
  // Pass window.location.pathname explicitly so we parse the actual current URL,
  // not globalState — we need the real path segments to compute the sub-path to preserve.
  const currentBasePath = getBasePathFromUrl(window.location.pathname);

  if (currentBasePath === newBasePath && matchPath(`${currentBasePath}/*`, window.location.pathname)) {
    return;
  }

  const currentPath = window.location.pathname;
  const hasBaseSegment = currentPath === currentBasePath || currentPath.startsWith(`${currentBasePath}/`);
  const subPath = hasBaseSegment ? currentPath.slice(currentBasePath.length) : currentPath;
  const newUrl = `${newBasePath}${subPath}${window.location.search}${window.location.hash}`;

  setBasePath(newBasePath);

  history.replace(newUrl, { rebasedFrom: currentBasePath });
}

export function replaceProductType(productType: ProductType) {
  const newBasePath = `/${productType.toLowerCase()}`;
  return replaceBasePath(newBasePath);
}

export function getProductTypeFromBasePath() {
  const base = getBasePathFromUrl();
  if (base === FINANCE_BASE) return Product.Finance;
  if (base === PROFESSIONAL_BASE) return Product.Professional;
  return undefined;
}
